import { Grid, Typography, Box  } from "@mui/material";
import {
    useCallback,
    useEffect,
    useState
} from "react";

import {
    Create,
    Datagrid,
    DeleteButton,
    EditButton,
    Edit,
    FunctionField,
    List,
    NumberInput,
    Pagination,
    SimpleForm,
    SaveButton,
    SelectInput,
    TextField,
    TextInput,
    Toolbar,
    useCreate,
    useDataProvider,
    useRecordContext
}
from "react-admin";

const CHALLENGE_TYPE_CHOICES = [
    { id: "self_assessment", name: "Self Assessment" },
    { id: "brush_data", name: "Brush Data" },
    { id: "learning", name: "Learning" },
    { id: "daily_log", name: "Daily Log" },
];


const ChallengeListFilters = [
    <TextInput label="Challenge Name" source="name__icontains" alwaysOn />,
    <SelectInput source="status" label="Status" choices={[
      { id: "active", name: "Active" },
      { id: "deleted", name: "Deleted" },
      { id: "inactive", name: "Inactive" },
    ]} fullWidth />
];

const MyPagination = () => <Pagination rowsPerPageOptions={[10,20,30]} />;

type ChallengeRecord = {
  successCriteria?: string | Record<string, unknown> | unknown[] | null;
};

export const ChallengeList = () => (
    <List filters={ChallengeListFilters} pagination={<MyPagination />}>
        <Datagrid>
            <TextField label="name" source="name"/>
            <TextField label="status"  source="status"/>
            <TextField label="description" source="description"/>
            <TextField label="type" source="type"/>
            <FunctionField
                label="Success Criteria"
                render={(record: ChallengeRecord) => record.successCriteria ? JSON.stringify(record.successCriteria) : ''}
            />
            <TextField label="version" source="version"/>
            <EditButton/>
        </Datagrid>
    </List>
);


const validateChallengeCreation = (values: any) => {
    const errors: any = {};
    if (!values.name) {
        errors.name = "Name is required";
    }
    return errors;
};


const ChallengeCreateEditForm = () => {
  const dataProvider = useDataProvider() as any;
  const [statusChoices, setStatusChoices] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let mounted = true;
    dataProvider
      .getChallengeStatusOptions()
      .then((res: any) => {
        if (mounted) setStatusChoices(res.data || []);
      })
      .catch(() => {
        if (mounted) setStatusChoices([]);
      });

    return () => {
      mounted = false;
    };
  }, [dataProvider]);

  return (
    <Grid container columnSpacing={2}>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={8}>
        <TextInput source="name" label="Name" fullWidth />
        <SelectInput source="status" label="Status" choices={statusChoices} fullWidth />
        <TextInput source="description" label="Description" fullWidth />
        <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
                Success Criteria
            </Typography>
            <SelectInput
                source="successCriteria.type"
                label="Criteria Type"
                choices={CHALLENGE_TYPE_CHOICES}
                fullWidth
            />
            <NumberInput
                source="successCriteria.target_sessions"
                label="Target Sessions"
                fullWidth
                helperText="Number of sessions required (e.g. 14)"
            />
            <NumberInput
                source="successCriteria.min_duration"
                label="Min Duration (seconds)"
                fullWidth
                helperText="Optional — sessions shorter than this don't count"
            />
            <NumberInput
                source="successCriteria.period_days"
                label="Period (days)"
                fullWidth
                helperText="Optional — only count sessions in the last N days"
            />
        </Box>
        <NumberInput source="version" label="Version" fullWidth />
      </Grid>
    </Grid>
  );
};

export const ChallengeCreate = () => {
    const [create] = useCreate();
    const save = useCallback(
        async (values: any) => {
            try {
                await create("challenge", {data: values}, {returnPromise: true });
            } catch (error: any) {
                if(error!.body!.errors) {
                    return error.body.errors;
                }
            }
        },
        [create]
    );

    return (
        <Create>
            <SimpleForm validate={validateChallengeCreation}>
                <ChallengeCreateEditForm />
            </SimpleForm>
        </Create>
    )
}

export const ChallengeEdit = () => {
    return (
        <Edit>
            <SimpleForm validate={validateChallengeCreation}>
                <ChallengeCreateEditForm />
            </SimpleForm>
        </Edit>
    )
}

const MyToolbar = (props: any) => {
    const record = useRecordContext();

    return (
        <Toolbar {...props} sx={{ justifyContent: "space-between" }}>
            <SaveButton label="Update"/>
            <DeleteButton label="Delete" sx={{ textAlign: "right" }} />
        </Toolbar>
    )

}
